import { useState } from "react";

import { Field } from "@/components/field";
import { NativeSelect } from "@/components/native-select";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { type CalibrationRequest, useCalibrateAssessment } from "@/lib/api";
import { logger } from "@/lib/logger";

const POSITIONS = [
  "Full Transformation",
  "Workflow Redesign",
  "Strategic Reinvention",
  "Fortify & Monitor",
];

const FEEDBACK_FIELDS = [
  ["what_felt_right", "What felt right?"],
  ["what_felt_wrong", "What felt wrong?"],
  ["client_objections", "What did the client challenge?"],
  ["examples_that_resonated", "Which examples resonated?"],
  ["recommended_copy_changes", "Most commercially relevant recommendation / copy changes"],
  ["follow_up_interest", "Interest in a follow-up offer?"],
  ["recommended_scoring_changes", "What should the index learn?"],
] as const;

interface CalibrationFormProps {
  assessmentId: string;
  defaults: {
    businessModel: number;
    operatingModel: number;
    protector: number;
    strategicPosition: string;
  };
}

export function CalibrationForm({ assessmentId, defaults }: CalibrationFormProps) {
  const calibrateMutation = useCalibrateAssessment(assessmentId);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    expert_name: "",
    diagnosis_accuracy_rating: "3",
    adjusted_business_model_score: "",
    adjusted_operating_model_score: "",
    adjusted_structural_protector_score: "",
    adjusted_strategic_position: "",
    what_felt_right: "",
    what_felt_wrong: "",
    client_objections: "",
    examples_that_resonated: "",
    recommended_copy_changes: "",
    follow_up_interest: "",
    recommended_scoring_changes: "",
  });

  function set(key: keyof typeof form, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function numOrUndef(v: string): number | undefined {
    if (v.trim() === "") {
      return undefined;
    }
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.expert_name.trim()) {
      setError("Your name is required.");
      return;
    }

    const payload: CalibrationRequest = {
      expert_name: form.expert_name,
      diagnosis_accuracy_rating: Number(form.diagnosis_accuracy_rating),
      adjusted_business_model_score: numOrUndef(form.adjusted_business_model_score),
      adjusted_operating_model_score: numOrUndef(form.adjusted_operating_model_score),
      adjusted_structural_protector_score: numOrUndef(form.adjusted_structural_protector_score),
      // The select only offers the four StrategicPosition literals (or "").
      adjusted_strategic_position: (form.adjusted_strategic_position ||
        undefined) as CalibrationRequest["adjusted_strategic_position"],
      what_felt_right: form.what_felt_right || undefined,
      what_felt_wrong: form.what_felt_wrong || undefined,
      client_objections: form.client_objections || undefined,
      examples_that_resonated: form.examples_that_resonated || undefined,
      recommended_copy_changes: form.recommended_copy_changes || undefined,
      follow_up_interest: form.follow_up_interest || undefined,
      recommended_scoring_changes: form.recommended_scoring_changes || undefined,
    };

    try {
      await calibrateMutation.mutateAsync(payload);
      setDone(true);
    } catch (err) {
      logger.debug("[expert] calibration failed:", err);
      const detail = (err as { body?: { detail?: string } } | undefined)?.body?.detail;
      setError(typeof detail === "string" ? detail : "Calibration failed.");
    }
  }

  return (
    <Card className="mt-5">
      <CardContent className="pt-6">
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
          {error && (
            <div className="border-destructive bg-destructive/10 text-destructive rounded-md border px-4 py-3 text-sm">
              {error}
            </div>
          )}
          {done && (
            <div className="border-accent bg-accent-light/30 text-primary rounded-md border px-4 py-3 text-sm">
              Calibration saved. It’s reflected in this internal expert view. The public client
              report remains the original system report.
            </div>
          )}

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Your name *">
              <Input
                type="text"
                value={form.expert_name}
                onChange={(e) => set("expert_name", e.target.value)}
              />
            </Field>
            <Field label="Did the diagnosis feel accurate? (1 to 5)">
              <NativeSelect
                value={form.diagnosis_accuracy_rating}
                onChange={(e) => set("diagnosis_accuracy_rating", e.target.value)}
              >
                <option value="1">1 (very inaccurate)</option>
                <option value="2">2</option>
                <option value="3">3 (mixed)</option>
                <option value="4">4</option>
                <option value="5">5 (very accurate)</option>
              </NativeSelect>
            </Field>
          </div>

          <div>
            <h3 className="text-primary text-xl font-normal tracking-tight">Score adjustments</h3>
            <p className="text-muted mt-1 text-xs">Leave blank to keep the system scores.</p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Business model" hint={`System: ${defaults.businessModel}`}>
              <Input
                type="text"
                inputMode="decimal"
                placeholder={String(defaults.businessModel)}
                value={form.adjusted_business_model_score}
                onChange={(e) => set("adjusted_business_model_score", e.target.value)}
              />
            </Field>
            <Field label="Operating model" hint={`System: ${defaults.operatingModel}`}>
              <Input
                type="text"
                inputMode="decimal"
                placeholder={String(defaults.operatingModel)}
                value={form.adjusted_operating_model_score}
                onChange={(e) => set("adjusted_operating_model_score", e.target.value)}
              />
            </Field>
            <Field label="Structural protectors" hint={`System: ${defaults.protector}`}>
              <Input
                type="text"
                inputMode="decimal"
                placeholder={String(defaults.protector)}
                value={form.adjusted_structural_protector_score}
                onChange={(e) => set("adjusted_structural_protector_score", e.target.value)}
              />
            </Field>
            <Field label="Strategic position" hint={`System: ${defaults.strategicPosition}`}>
              <NativeSelect
                value={form.adjusted_strategic_position}
                onChange={(e) => set("adjusted_strategic_position", e.target.value)}
              >
                <option value="">(no change)</option>
                {POSITIONS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </div>

          <h3 className="text-primary text-xl font-normal tracking-tight">Session feedback</h3>
          {FEEDBACK_FIELDS.map(([key, label]) => (
            <Field key={key} label={label}>
              <Textarea
                className="bg-card min-h-20"
                value={form[key]}
                onChange={(e) => set(key, e.target.value)}
              />
            </Field>
          ))}

          <Button type="submit" disabled={calibrateMutation.isPending}>
            {calibrateMutation.isPending ? "Saving…" : "Save calibration"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
